import dagre from "dagre";
import { Edge, Node } from "@xyflow/react";

import { CompanyOrgStructureDocument, OrgNodeKind } from "./orgStructureTypes";

const NODE_WIDTH = 220;
const NODE_HEIGHT = 96;

export interface OrgStructureNodeData {
    nodeId: string;
    kind: OrgNodeKind;
    title: string;
    description: string;
    peopleCount: number;
    firstPersonName: string;
    selected: boolean;
    [key: string]: unknown;
}

function buildEdgePairs(document: CompanyOrgStructureDocument): Array<[string, string]> {
    const pairs: Array<[string, string]> = [];
    for (const node of document.nodes) {
        if (node.parent_id !== null) {
            pairs.push([node.parent_id, node.id]);
        }
    }
    return pairs;
}

export function orgStructureToFlow(
    document: CompanyOrgStructureDocument,
    selectedNodeId: string | null
): { nodes: Node<OrgStructureNodeData>[]; edges: Edge[] } {
    const nodes: Node<OrgStructureNodeData>[] = [];
    const edges: Edge[] = [];

    if (document.nodes.length === 0) {
        return { nodes, edges };
    }

    const graph = new dagre.graphlib.Graph();
    graph.setDefaultEdgeLabel(() => ({}));
    graph.setGraph({ rankdir: "TB", nodesep: 48, ranksep: 72, marginx: 16, marginy: 16 });

    for (const node of document.nodes) {
        graph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
    }

    for (const [source, target] of buildEdgePairs(document)) {
        graph.setEdge(source, target);
    }

    dagre.layout(graph);

    for (const node of document.nodes) {
        const position = graph.node(node.id);
        if (!position) {
            continue;
        }
        const firstPerson = node.people[0];
        nodes.push({
            id: node.id,
            type: node.kind,
            position: {
                x: position.x - NODE_WIDTH / 2,
                y: position.y - NODE_HEIGHT / 2
            },
            data: {
                nodeId: node.id,
                kind: node.kind,
                title: node.title,
                description: node.description,
                peopleCount: node.people.length,
                firstPersonName: firstPerson?.name ?? "",
                selected: selectedNodeId === node.id
            },
            selectable: true,
            draggable: false
        });
    }

    for (const [source, target] of buildEdgePairs(document)) {
        edges.push({
            id: `${source}->${target}`,
            source,
            target,
            type: "smoothstep",
            selectable: false
        });
    }

    return { nodes, edges };
}
